import { useMemo, useState } from 'react';
import { closeInterface } from '../../services/InterfaceService';
import { useInventoryStore } from '../../store/inventorySlice';
import { ITEMS } from '../../data/inventory.config';
import { MODIFIER_REGISTRY } from '../../data/modifiers.config';
import {
  PLAYER_STAT_DESCRIPTORS,
  formatStatValue,
} from '../../data/stat-descriptions.config';
import { ItemType } from '../../types/inventory.types';
import { dungeonService } from '../../services/DungeonService';
import { runStartProgressionService } from '../../services/RunStartProgressionService';
import { SocketService } from '../../services/SocketService';
import { useLoadoutBoardData } from '../../hooks/useLoadoutBoardData';
import { LoadoutBoard, type BoardRef } from './LoadoutBoard';
import { ItemList, ItemListEntry } from './ItemList';
import { NineSliceFrame } from './NineSliceFrame';
import { KeyDisplay } from './KeyDisplay';
import './EquipmentScreen.scss';

const BOOST_COLS = 4;
const BOOST_ROWS = 4;

/**
 * In-run equipment page: shows the equipped loadout (every cell is hoverable to
 * review its detail card) but only trinket sockets are actionable — to equip the
 * temporary boosts held in the inventory. Equipping consumes the boost item;
 * unequipping returns it (cf. SocketService.equipBoostFromInventory /
 * unequipBoostToInventory). A right-hand column lists the player's current stats.
 */
export const EquipmentScreen = () => {
  const [socketing, setSocketing] = useState<{
    minorId: string;
    socketIndex: number;
  } | null>(null);
  const [boardFocus, setBoardFocus] = useState<BoardRef | null>(null);

  const returnToBoard = () => {
    if (socketing) {
      setBoardFocus({
        kind: 'socket',
        minorId: socketing.minorId,
        socketIndex: socketing.socketIndex,
      });
    }
    setSocketing(null);
  };

  const { slots, sockets, getCard } = useLoadoutBoardData();
  const inventorySlots = useInventoryStore((s) => s.slots);

  const socketingActive = socketing !== null;

  const player = dungeonService.getPlayer();
  const playerStats = player
    ? player.stats
    : runStartProgressionService.buildRunStats().stats;

  const boostItems: ItemListEntry[] = useMemo(() => {
    const list: ItemListEntry[] = [];
    for (const slot of inventorySlots) {
      const def = slot ? ITEMS[slot.itemId] : null;
      if (!slot || !def || def.type !== ItemType.BOOST || !def.modifierId) {
        continue;
      }
      const mod = MODIFIER_REGISTRY[def.modifierId];
      list.push({
        id: slot.itemId,
        navId: `boost:${slot.itemId}`,
        icon: def.sprite,
        quantity: slot.quantity,
        card: {
          icon: def.sprite,
          name: def.label,
          subtitle: mod?.displayTemplate,
        },
      });
    }
    return list;
  }, [inventorySlots]);

  const onBoardActivate = (ref: BoardRef): boolean => {
    if (ref.kind !== 'socket') {
      return false;
    }
    const socketedIds = SocketService.getSockets(ref.minorId);
    if (
      socketedIds[ref.socketIndex] == null &&
      SocketService.canSocket(ref.minorId)
    ) {
      setSocketing({
        minorId: ref.minorId,
        socketIndex: ref.socketIndex,
      });
      return true;
    }
    // Socket already filled or this minor can't take more boosts → denied.
    return false;
  };

  const onBoardSecondary = (ref: BoardRef) => {
    if (ref.kind !== 'socket') {
      return;
    }
    const modifierId = SocketService.getSockets(ref.minorId)[ref.socketIndex];
    if (modifierId) {
      SocketService.unequipBoostToInventory(ref.minorId, modifierId);
    }
  };

  const onBoostConfirm = (item: ItemListEntry): boolean => {
    if (!socketing) {
      return false;
    }
    const itemId = item.navId.slice('boost:'.length);
    const def = ITEMS[itemId];
    if (def?.modifierId) {
      SocketService.equipBoostFromInventory(
        socketing.minorId,
        socketing.socketIndex,
        def.modifierId,
      );
      returnToBoard();
      return true;
    }
    returnToBoard();
    return false;
  };

  const onBoostCancel = () => {
    returnToBoard();
  };

  return (
    <div className="equipment-overlay">
      <NineSliceFrame className="equipment-frame">
        <div className="equipment-screen">
          <h1 className="equipment-title">Équipement</h1>
          <div className="equipment-body">
            <div className="equipment-board-wrap">
              <LoadoutBoard
                active={!socketingActive}
                slots={slots}
                sockets={sockets}
                getCard={getCard}
                focusTarget={!socketingActive ? boardFocus : null}
                onActivate={onBoardActivate}
                onSecondary={onBoardSecondary}
                onCancel={closeInterface}
              />
            </div>
            <div className="equipment-boosts">
              <h3 className="equipment-boosts-title">Boosts</h3>
              <ItemList
                active={socketingActive}
                items={boostItems}
                cols={BOOST_COLS}
                maxRows={BOOST_ROWS}
                onConfirm={onBoostConfirm}
                onCancel={onBoostCancel}
              />
            </div>
            <div className="equipment-stats">
              <h3 className="equipment-stats-title">Statistiques</h3>
              <ul className="equipment-stats-list">
                {PLAYER_STAT_DESCRIPTORS.map((d) => (
                  <li key={d.key} className="equipment-stat">
                    <div className="equipment-stat-head">
                      <span className="equipment-stat-label">{d.label}</span>
                      <span className="equipment-stat-value">
                        {formatStatValue(playerStats[d.key], d.format)}
                      </span>
                    </div>
                    <div className="equipment-stat-desc">{d.description}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="equipment-hint">
            <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
            {socketingActive ? 'équiper le boost' : 'choisir un socket'}{' '}
            &nbsp;·&nbsp;{' '}
            <KeyDisplay context="interface" action="SECONDARY" size="sm" />{' '}
            retirer le boost &nbsp;·&nbsp;{' '}
            <KeyDisplay context="interface" action="CANCEL" size="sm" /> retour
          </div>
        </div>
      </NineSliceFrame>
    </div>
  );
};
